import { urlParams } from '@/app/urlParams';
import { GAME_SESSION_STATUS } from '@/constants/maze';
import type {
  EndBoardResponse,
  EndGameResponse,
  GameMazeDto,
  GameSessionResponse,
  GameSessionStatusCode,
} from '@/types/api/game';

import {
  type BoardDifficulty,
  getGameConfig,
  mapRawBoardToGameMazeDto,
  selectSessionBoards,
  type SelectedBoard,
} from './board-pool';
import { aggregateSessionScore, type RoundResult } from './score-engine';

interface RoundAdvanceResult {
  nextMaze: GameMazeDto | null;
  endBoard: EndBoardResponse;
  endGame: EndGameResponse | null;
}

/**
 * Local game engine — manages a complete offline game session in module-level memory.
 * Replaces all backend calls (game-start, submit-moves, next-board, end-board, end-game,
 * status). No persistence: every `startGame` call re-randomizes boards and resets state,
 * and a page refresh naturally clears everything.
 */
class LiveGameEngine {
  private boards: SelectedBoard[] = [];
  private currentRoundIndex = 0;
  private rounds: RoundResult[] = [];
  private expiryAtMs = 0;
  private startedAtMs = 0;
  private enableTimer = true;
  private timeLimitSeconds = 0;
  private maxTimeBonus = 0;
  private status: GameSessionStatusCode = GAME_SESSION_STATUS.STARTED;
  private sessionId = '';
  private hasSession = false;

  private resolveEnableTimer(configEnableTimer: boolean): boolean {
    if (urlParams.timer !== '') {
      return urlParams.timer === 'true';
    }
    return configEnableTimer;
  }

  private resolveTimeLimitSeconds(configTimeLimit: number): number {
    if (urlParams.timeLimit !== '') {
      const parsed = Number(urlParams.timeLimit);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return configTimeLimit;
  }

  private currentMazeDto(): GameMazeDto | null {
    const board = this.boards[this.currentRoundIndex];
    if (!board) {
      return null;
    }
    return mapRawBoardToGameMazeDto(board, { roundNumber: this.currentRoundIndex + 1 });
  }

  private buildSessionResponse(): GameSessionResponse {
    const { roundsScore } = aggregateSessionScore({
      rounds: this.rounds,
      totalRounds: this.boards.length,
      timeLimitSeconds: this.timeLimitSeconds,
      maxTimeBonus: this.maxTimeBonus,
      remainingMsAtLastRoundEnd: 0,
      timerEnabled: this.enableTimer,
    });
    return {
      status: this.status,
      sessionId: this.sessionId,
      startedAt: new Date(this.startedAtMs).toISOString(),
      expiryAt: new Date(this.expiryAtMs).toISOString(),
      totalRounds: this.boards.length,
      // A skipped round doesn't count as "completed" — only rounds that reached the goal do.
      completedBoards: this.rounds.filter((r) => r.reachedEnd).length,
      maze: this.currentMazeDto(),
      scoreboard: { totalScore: roundsScore, timeBonus: 0, rounds: [] },
    };
  }

  startGame(difficulty: BoardDifficulty): GameSessionResponse {
    const config = getGameConfig();
    this.boards = selectSessionBoards(difficulty);
    this.currentRoundIndex = 0;
    this.rounds = [];
    this.enableTimer = this.resolveEnableTimer(config.enableTimer);
    this.timeLimitSeconds = this.resolveTimeLimitSeconds(config.timeLimit);
    this.maxTimeBonus = config.maxTimeBonus;
    this.startedAtMs = Date.now();
    this.expiryAtMs = this.startedAtMs + this.timeLimitSeconds * 1000;
    this.status = GAME_SESSION_STATUS.STARTED;
    this.sessionId = crypto.randomUUID();
    this.hasSession = true;
    return this.buildSessionResponse();
  }

  isTimerEnabled(): boolean {
    return this.enableTimer;
  }

  getStatus(): GameSessionResponse | null {
    if (!this.hasSession) {
      return null;
    }
    return this.buildSessionResponse();
  }

  private finalize(): { totalScore: number; timeBonus: number } {
    const { timeBonus, totalScore } = aggregateSessionScore({
      rounds: this.rounds,
      totalRounds: this.boards.length,
      timeLimitSeconds: this.timeLimitSeconds,
      maxTimeBonus: this.maxTimeBonus,
      remainingMsAtLastRoundEnd: Math.max(0, this.expiryAtMs - Date.now()),
      timerEnabled: this.enableTimer,
    });
    this.status = GAME_SESSION_STATUS.ENDED;
    return { totalScore, timeBonus };
  }

  private advance(reachedEnd: boolean, skipped: boolean): RoundAdvanceResult {
    const board = this.boards[this.currentRoundIndex];
    const maxScore = board?.maxScore ?? 0;
    this.rounds.push({ reachedEnd, maxScore, skipped });
    this.currentRoundIndex += 1;

    const isLastBoard = this.currentRoundIndex >= this.boards.length;
    const roundScore = skipped ? 0 : reachedEnd ? maxScore : 0;

    if (!isLastBoard) {
      return {
        nextMaze: this.currentMazeDto(),
        endBoard: { gameOver: false, roundScore },
        endGame: null,
      };
    }

    const { totalScore, timeBonus } = this.finalize();
    return {
      nextMaze: null,
      endBoard: { gameOver: true, roundScore },
      endGame: { status: GAME_SESSION_STATUS.ENDED, totalScore, timeBonus },
    };
  }

  advanceRoundAfterReachingEnd(): RoundAdvanceResult {
    return this.advance(true, false);
  }

  skipRound(): RoundAdvanceResult {
    return this.advance(false, true);
  }

  endGameEarly(): EndGameResponse | null {
    if (!this.hasSession || this.status !== GAME_SESSION_STATUS.STARTED) {
      return null;
    }
    this.status = GAME_SESSION_STATUS.MANUALLY_ENDED;
    const { totalScore, timeBonus } = aggregateSessionScore({
      rounds: this.rounds,
      totalRounds: this.boards.length,
      timeLimitSeconds: this.timeLimitSeconds,
      maxTimeBonus: this.maxTimeBonus,
      remainingMsAtLastRoundEnd: Math.max(0, this.expiryAtMs - Date.now()),
      timerEnabled: this.enableTimer,
    });
    return { status: GAME_SESSION_STATUS.MANUALLY_ENDED, totalScore, timeBonus };
  }

  /** No-op: move submission is anti-cheat bookkeeping only in the backend, not needed locally. */
  submitMoves(): void {
    // intentionally empty
  }

  reset(): void {
    this.boards = [];
    this.currentRoundIndex = 0;
    this.rounds = [];
    this.hasSession = false;
    this.status = GAME_SESSION_STATUS.STARTED;
  }
}

export const liveGameEngine = new LiveGameEngine();
